import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, UserCircle, LogOut } from "lucide-react";
import { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "Services", href: "#services" },
  { label: "Consulting", href: "#consulting" },
  { label: "Insights", href: "/content", testid: "nav-insights" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

const clientLinks = [
  { label: "Register for an account", href: "/register", testid: "clients-register" },
  { label: "Initial Assessment", href: "/assessment", testid: "clients-assessment" },
  { label: "Log In", href: "/portal/login", testid: "clients-login" },
];

export default function Header({ onBook }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [clientsOpen, setClientsOpen] = useState(false);
  const [logo, setLogo] = useState("");
  const dropRef = useRef(null);
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  // When we're anywhere other than the landing page, in-page anchor links (#services,
  // #contact, …) can't scroll to sections that only exist on `/`. Rewrite them to point at
  // the landing route + hash so the browser navigates home and then jumps to the section.
  const resolveHref = (href) => (href.startsWith("#") && pathname !== "/" ? `/${href}` : href);
  const isClient = !!user && (user.role === "client" || (user.roles || []).includes("client"));
  const clientName = (user && (user.name || user.full_name || user.email)) || "Client";
  const firstName = clientName.split(" ")[0].split("@")[0];
  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content`).then((r) => r.json()).then((d) => setLogo(resolveImg(d.logo_url || ""))).catch(() => {});
    const onClick = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setClientsOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { window.removeEventListener("scroll", onScroll); document.removeEventListener("mousedown", onClick); };
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 bg-upland-offwhite/85 backdrop-blur-xl border-b border-upland-brown/20 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <a href={pathname === "/" ? "#top" : "/"} data-testid="logo-link" className="flex items-center gap-3 group shrink-0 min-w-0">
          {logo ? (
            <img src={logo} alt="Upland Group" loading="eager" decoding="async"
              className="h-10 md:h-11 w-auto max-w-[44px] object-contain shrink-0 transition-transform duration-300 group-hover:scale-105" />
          ) : (
            <span className="h-9 w-9 shrink-0 grid place-items-center border border-upland-green text-upland-green font-serif text-lg leading-none">U</span>
          )}
          <span className="font-serif text-lg sm:text-xl tracking-tight text-upland-green whitespace-nowrap">Upland Group</span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a key={l.href} href={resolveHref(l.href)} data-testid={l.testid || `nav-${l.label.toLowerCase()}`}
               className="link-underline text-sm tracking-wide text-upland-green/80 hover:text-upland-green">
              {l.label}
            </a>
          ))}

          <div className="relative" ref={dropRef}>
            {isClient ? (
              <>
                <button data-testid="client-chip-btn" onClick={() => setClientsOpen((v) => !v)}
                  className="flex items-center gap-2 text-sm tracking-wide border border-upland-green/40 text-upland-green pl-2 pr-3 py-2 hover:bg-upland-green hover:text-upland-creme transition-colors duration-300">
                  <UserCircle size={20} />
                  <span className="max-w-[100px] truncate" data-testid="client-chip-name">{firstName}</span>
                  <ChevronDown size={15} className={`transition-transform ${clientsOpen ? "rotate-180" : ""}`} />
                </button>
                {clientsOpen && (
                  <div data-testid="client-menu" className="absolute right-0 mt-2 w-56 bg-upland-offwhite border border-upland-brown/30 shadow-lg py-1.5">
                    <a href="/portal" data-testid="clients-portal"
                      className="block px-4 py-2.5 text-sm text-upland-green hover:bg-upland-green hover:text-upland-creme transition-colors">
                      My client portal
                    </a>
                    <button onClick={handleLogout} data-testid="client-logout-btn"
                      className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-sm text-upland-green hover:bg-upland-green hover:text-upland-creme transition-colors">
                      <LogOut size={15} /> Log out
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <button data-testid="clients-menu-btn" onClick={() => setClientsOpen((v) => !v)}
                  className="flex items-center gap-1.5 text-sm tracking-wide border border-upland-green/40 text-upland-green px-5 py-3 hover:bg-upland-green hover:text-upland-creme transition-colors duration-300">
                  Clients <ChevronDown size={15} className={`transition-transform ${clientsOpen ? "rotate-180" : ""}`} />
                </button>
                {clientsOpen && (
                  <div data-testid="clients-menu" className="absolute right-0 mt-2 w-56 bg-upland-offwhite border border-upland-brown/30 shadow-lg py-1.5">
                    {clientLinks.map((c) => (
                      <a key={c.href} href={c.href} data-testid={c.testid}
                        className="block px-4 py-2.5 text-sm text-upland-green hover:bg-upland-green hover:text-upland-creme transition-colors">
                        {c.label}
                      </a>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <button data-testid="header-book-btn" onClick={onBook}
            className="bg-upland-green text-upland-creme text-sm px-6 py-3 hover:bg-upland-brown transition-colors duration-300">
            Book a Consultation
          </button>
        </nav>

        <button className="md:hidden text-upland-green" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-upland-offwhite border-t border-upland-brown/20 px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <a key={l.href} href={resolveHref(l.href)} onClick={() => setOpen(false)} className="text-upland-green text-lg font-serif">{l.label}</a>
          ))}
          <p className="text-xs uppercase tracking-[0.2em] text-upland-brown mt-2">Clients</p>
          {isClient ? (
            <>
              <div className="flex items-center gap-2 text-upland-green text-base pl-3 border-l-2 border-upland-brown/30" data-testid="mobile-client-chip">
                <UserCircle size={20} /> {firstName}
              </div>
              <a href="/portal" data-testid="mobile-clients-portal" onClick={() => setOpen(false)}
                className="text-upland-green text-base pl-3 border-l-2 border-upland-brown/30">My client portal</a>
              <button data-testid="mobile-client-logout-btn" onClick={() => { setOpen(false); handleLogout(); }}
                className="flex items-center gap-2 text-upland-green text-base pl-3 border-l-2 border-upland-brown/30">
                <LogOut size={16} /> Log out
              </button>
            </>
          ) : (
            clientLinks.map((c) => (
              <a key={c.href} href={c.href} data-testid={`mobile-${c.testid}`} onClick={() => setOpen(false)}
                className="text-upland-green text-base pl-3 border-l-2 border-upland-brown/30">{c.label}</a>
            ))
          )}
          <button data-testid="mobile-book-btn" onClick={() => { setOpen(false); onBook(); }}
            className="bg-upland-green text-upland-creme py-3 mt-2">Book a Consultation</button>
        </div>
      )}
    </header>
  );
}
